
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trophy, Medal, Award, RotateCcw, Clock } from 'lucide-react';
import { format } from 'date-fns';

interface LeaderboardPlayer {
  id: string;
  name: string;
  points: number;
  gamesPlayed: number;
  roundScores?: number[];
  matchesPlayed?: number;
  joinedAt?: Date;
}

interface TournamentLeaderboardProps {
  players: LeaderboardPlayer[];
  currentRound: number;
  isFinished: boolean;
  tournamentType: 'mexicano' | 'americano';
  onRestartTournament?: () => void;
  onFinishTournament?: () => void;
}

export const TournamentLeaderboard: React.FC<TournamentLeaderboardProps> = ({
  players,
  currentRound,
  isFinished,
  tournamentType,
  onRestartTournament,
  onFinishTournament
}) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [showSignupTimes, setShowSignupTimes] = React.useState(false);

  const leaderboard = [...players].sort((a, b) => {
    // For Mexicano: sort by total match points scored (roundScores) only
    if (tournamentType === 'mexicano') {
      const aTotalMatchPoints = (a.roundScores || []).reduce((sum, score) => sum + score, 0);
      const bTotalMatchPoints = (b.roundScores || []).reduce((sum, score) => sum + score, 0);
      
      // Primary sort: total match points scored across all rounds
      if (bTotalMatchPoints !== aTotalMatchPoints) {
        return bTotalMatchPoints - aTotalMatchPoints;
      }
      
      // Tiebreaker: matches played
      return (b.gamesPlayed || b.matchesPlayed || 0) - (a.gamesPlayed || a.matchesPlayed || 0);
    }
    
    // For Americano: sort by tournament points, then by matches played
    if (b.points !== a.points) return b.points - a.points;
    return (b.gamesPlayed || b.matchesPlayed || 0) - (a.gamesPlayed || a.matchesPlayed || 0);
  });

  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Trophy className="h-4 w-4 text-yellow-500" />;
      case 2:
        return <Medal className="h-4 w-4 text-gray-400" />;
      case 3:
        return <Award className="h-4 w-4 text-orange-600" />;
      default:
        return null;
    }
  };

  const getPositionBadge = (position: number) => {
    if (position <= 3) {
      const colors = ['bg-yellow-100 text-yellow-800', 'bg-gray-100 text-gray-800', 'bg-orange-100 text-orange-800'];
      return (
        <Badge variant="secondary" className={colors[position - 1]}>
          {position}
        </Badge>
      );
    }
    return <span className="font-semibold">{position}</span>;
  };

  const completedRounds = currentRound - (isFinished ? 0 : 1);
  // For Mexicano, show headers up to the current round (including the active one)
  const roundHeaderCount = tournamentType === 'mexicano'
    ? Math.max(1, currentRound)
    : Math.max(0, completedRounds);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-green-500" />
            {isFinished 
              ? (language === 'no' ? 'Sluttresultat' : 'Final Results')
              : (language === 'no' ? 'Resultater' : 'Leaderboard')
            }
          </CardTitle>
          {players.some(p => p.joinedAt) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSignupTimes(!showSignupTimes)}
              className="flex items-center gap-2"
            >
              <Clock className="h-4 w-4" />
              {showSignupTimes ? 'Hide Times' : 'Show Times'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">
                  {language === 'no' ? 'Plass' : 'Pos'}
                </TableHead>
                <TableHead>{language === 'no' ? 'Spiller' : 'Player'}</TableHead>
                <TableHead className="text-center font-bold">
                  {language === 'no' ? 'Total' : 'Total'}
                </TableHead>
                {Array.from({ length: roundHeaderCount }, (_, i) => (
                  <TableHead key={i} className="text-center">
                    {language === 'no' ? `R${i + 1}` : `R${i + 1}`}
                  </TableHead>
                ))}
                {tournamentType === 'americano' && (
                  <TableHead className="text-center">
                    {language === 'no' ? 'Kamper' : 'Matches'}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaderboard.map((player, index) => {
                const position = index + 1;
                const roundScores = player.roundScores || [];
                const matchesPlayed = player.gamesPlayed || player.matchesPlayed || 0;
                
                return (
                  <TableRow key={player.id} className={position <= 3 ? 'bg-muted/50' : ''}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {getPositionBadge(position)}
                        {getPositionIcon(position)}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      <div>
                        <div>{player.name}</div>
                        {showSignupTimes && player.joinedAt && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Clock className="h-3 w-3" />
                            {format(new Date(player.joinedAt), 'MMM d, HH:mm:ss.SSS')}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={position === 1 ? 'default' : 'secondary'} className="font-bold">
                        {tournamentType === 'mexicano' 
                          ? (player.roundScores || []).reduce((sum, score) => sum + score, 0)
                          : player.points
                        }
                      </Badge>
                    </TableCell>
                    {Array.from({ length: roundHeaderCount }, (_, i) => (
                      <TableCell key={`${player.id}-round-${i}`} className="text-center">
                        {roundScores[i] !== undefined ? roundScores[i] : '-'}
                      </TableCell>
                    ))}
                    {tournamentType === 'americano' && (
                      <TableCell className="text-center">{matchesPlayed}</TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {isFinished && leaderboard.length > 0 && (() => {
          const winner = leaderboard[0];
          const winnerTotal = tournamentType === 'mexicano'
            ? (winner.roundScores || []).reduce((sum, s) => sum + s, 0)
            : winner.points;
          const winnerMatches = winner.gamesPlayed || winner.matchesPlayed || 0;
          return (
            <>
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 text-green-800">
                  <Trophy className="h-5 w-5" />
                  <span className="font-semibold">
                    {language === 'no' 
                      ? `🎉 Gratulerer til ${winner.name}!` 
                      : `🎉 Congratulations to ${winner.name}!`}
                  </span>
                </div>
                <p className="text-sm text-green-700 mt-1">
                  {language === 'no'
                    ? `Vant med ${winnerTotal} poeng på ${winnerMatches} kamper`
                    : `Won with ${winnerTotal} points in ${winnerMatches} games`}
                </p>
              </div>
              <div className="mt-4 flex gap-3">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="flex-1"
                >
                  {language === 'no' ? 'Tilbake til dashboard' : 'Back to Dashboard'}
                </Button>
                <Button
                  onClick={onRestartTournament}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={!onRestartTournament}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {language === 'no' ? 'Ny turnering' : 'New Tournament'}
                </Button>
              </div>
            </>
          );
        })()}

        {!isFinished && onFinishTournament && (
          <div className="mt-4">
            <Button onClick={onFinishTournament} className="w-full">
              {language === 'no' ? 'Avslutt turnering' : 'Finish Tournament'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
